import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';

export function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: 'Trang chủ', public: true },
    { path: '/pricing', label: 'Bảng giá', public: true },
    { path: '/dashboard', label: 'Bảng điều khiển', public: false },
    { path: '/admin', label: 'Quản trị', public: false, admin: true },
  ];

  const visibleLinks = navLinks.filter(
    (link) => link.public || (user && (!link.admin || isAdmin))
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-surface border-b border-border sticky top-0 z-50">
        <nav className="container flex items-center justify-between h-16" aria-label="Main navigation">
          <Link to="/" className="text-xl font-bold text-primary" aria-label="OCR Invoice Home">
            OCR Invoice
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {visibleLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path ? 'text-primary' : 'text-text-muted'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-sm text-text-muted">{user.email}</span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    user.planId === 'free'
                      ? 'bg-gray-100 text-gray-700'
                      : user.planId === 'plus'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {user.planId.toUpperCase()}
                </span>
                <button onClick={handleLogout} className="btn-ghost text-sm">
                  Đăng xuất
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="btn-ghost text-sm">
                  Đăng nhập
                </Link>
                <Link to="/register" className="btn-primary text-sm">
                  Đăng ký
                </Link>
              </div>
            )}
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </nav>

        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              {visibleLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'bg-primary-light text-primary' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <div className="pt-2 border-t border-border flex flex-col gap-2">
                  <span className="text-sm text-text-muted">{user.email}</span>
                  <button onClick={handleLogout} className="btn-outline text-left">
                    Đăng xuất
                  </button>
                </div>
              ) : (
                <div className="pt-2 border-t border-border flex flex-col gap-2">
                  <Link to="/login" className="btn-outline text-center" onClick={() => setMobileMenuOpen(false)}>
                    Đăng nhập
                  </Link>
                  <Link to="/register" className="btn-primary text-center" onClick={() => setMobileMenuOpen(false)}>
                    Đăng ký
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-semibold mb-4">OCR Invoice</h4>
              <p className="text-sm">Ứng dụng desktop trích xuất dữ liệu hóa đơn bằng AI vision. Nhanh, chính xác, bảo mật.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Sản phẩm</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Bảng giá</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors">Tài liệu</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Hỗ trợ</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/login" className="hover:text-white transition-colors">Đăng nhập</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Đăng ký</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Pháp lý</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Điều khoản</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Quyền riêng tư</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} OCR Invoice. All rights reserved.</p>
            <p className="mt-2 text-gray-500">
              Phát triển với ❤️ bằng{' '}
              <a href="https://tauri.app" className="text-blue-400 hover:underline">Tauri</a>
              {' '}và{' '}
              <a href="https://cloudflare.com/workers" className="text-blue-400 hover:underline">Cloudflare Workers</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}